import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppId, AppThunkAPIType } from "../../../app/appTypes";
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http";

export type DocumentWorkflowStageTypeEnum = "EDIT" | "REVIEW" | "APPROVAL" | number;
export type DocumentWorkflowStageStatusEnum = 'NOT_STARTED' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
export type DocumentWorkflowActionEnum = string | number;
export type DocumentWorkflowActionStatusEnum = string | number;
export type DocumentTeamMemberRoleEnum = "AUTHOR" | "EDITOR" | "REVIEWER" | "APPROVER" | number;

export interface DocumentWorkflowStageMemberDto {
	documentTeamMemberId: number;
	userName: string;
	fullName: string;
	position: string;
	companyName: string;
	role: DocumentTeamMemberRoleEnum;
	actionType?: DocumentWorkflowActionEnum | null;
	actionStatus?: DocumentWorkflowActionStatusEnum | null;
	actionResult: string;
	execDate?: string | null;
	deadline?: string | null;
}

export interface DocumentWorkflowActionDto {
	id: number;
	documentTeamMemberId: number;
	userName: string;
	fullName: string;
	actionType: DocumentWorkflowActionEnum;
	status: DocumentWorkflowActionStatusEnum;
	actionResult: string;
	createDate: string;
	execDate?: string | null;
	deadline?: string | null;
	comment?: string | null;
}

export interface DocumentWorkflowStageDto {
	id: number;
	name: string;
	description: string;
	type: DocumentWorkflowStageTypeEnum;
	status: DocumentWorkflowStageStatusEnum;
	order: number;
	isCurrent: boolean;
	documentTeamMemberIds: number[];
	members: DocumentWorkflowStageMemberDto[];
	actions: DocumentWorkflowActionDto[];
}

export interface DocumentTeamMemberDto {
	id: number;
	userName: string;
	fullName: string;
	position: string;
	companyName: string;
	roles?: DocumentTeamMemberRoleEnum[];
	role: DocumentTeamMemberRoleEnum | null;
	workflowStageIds: number[];
}

export interface DocumentWorkflowDefinitionDto {
	documentId: number;
	currentWorkflowStageId?: number | null;
	stages: DocumentWorkflowStageDto[];
	teamMembers: DocumentTeamMemberDto[];
}

export interface DocumentWorkflowTimelineItemDto {
	id: string;
	eventType: string;
	date?: string | null;
	sourceWorkflowStageId: number;
	stageName: string;
	stageType: DocumentWorkflowStageTypeEnum;
	iterationNo: number;
	actionType?: DocumentWorkflowActionEnum | null;
	actionResult?: string | null;
	userName: string;
	fullName: string;
	comment?: string | null;
}

export interface CreateUpdateDocumentWorkflowStageDto {
	name: string;
	description: string;
	type: DocumentWorkflowStageTypeEnum;
}

export interface AssignDocumentTeamMemberToStagesDto {
	userName: string;
	stageIds: number[];
}

export interface ChangeAuthorDocumentTeamMemberDto {
	userName: string;
	reason: string;
	password: string;
}

export interface ChangeTeamMemberStagesDto {
	addStageIds: number[];
	removeStageIds: number[];
}

export interface ChangeWorkflowActionDeadlineDto {
	stageId: AppId;
	deadline: string;
	forAllMembers: boolean;
	documentTeamMemberId?: AppId | null;
}

export interface SendWorkflowPendingActionReminderDto {
	stageId: AppId;
	forAllMembers: boolean;
	documentTeamMemberId?: AppId | null;
}

export const loadDocumentWorkflowDefinition = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	AppId,
	AppThunkAPIType
>(
	"document-workflow-definition/load",
	async (documentId, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			getWithAuth({
				url: `api/document-workflow-definition/${documentId}`,
			})
		);

		const { payload } = result;

		if (getWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const loadDocumentWorkflowTimeline = createAsyncThunk<
	DocumentWorkflowTimelineItemDto[],
	AppId,
	AppThunkAPIType
>(
	"document-workflow-definition/loadTimeline",
	async (documentId, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			getWithAuth({
				url: `api/document-workflow-definition/${documentId}/timeline`,
			})
		);

		const { payload } = result;

		if (getWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowTimelineItemDto[];
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const createDocumentWorkflowStage = createAsyncThunk<
	DocumentWorkflowStageDto,
	{ documentId: AppId; dto: CreateUpdateDocumentWorkflowStageDto },
	AppThunkAPIType
>(
	"document-workflow-definition/createDocumentWorkflowStage",
	async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/stages`,
				payload: dto,
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowStageDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const updateDocumentWorkflowStage = createAsyncThunk<
	DocumentWorkflowStageDto,
	{ documentId: AppId; stageId: AppId; dto: CreateUpdateDocumentWorkflowStageDto },
	AppThunkAPIType
>(
	"document-workflow-definition/updateDocumentWorkflowStage",
	async ({ documentId, stageId, dto }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			putWithAuth({
				url: `api/document-workflow-definition/${documentId}/stages/${stageId}`,
				payload: dto,
			})
		);

		const { payload } = result;

		if (putWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowStageDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const deleteDocumentWorkflowStage = createAsyncThunk<
	AppId,
	{ documentId: AppId; stageId: AppId },
	AppThunkAPIType
>(
	"document-workflow-definition/deleteDocumentWorkflowStage",
	async ({ documentId, stageId }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			deleteWithAuth({
				url: `api/document-workflow-definition/${documentId}/stages/${stageId}`,
			})
		);

		const { payload } = result;

		if (deleteWithAuth.fulfilled.match(result)) {
			return stageId;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const assignDocumentTeamMemberToStages = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{ documentId: AppId; dto: AssignDocumentTeamMemberToStagesDto },
	AppThunkAPIType
>(
	"document-workflow-definition/assignDocumentTeamMemberToStages",
	async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/team-members/add`,
				payload: dto,
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const changeAuthorDocumentTeamMember = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{ documentId: AppId; dto: ChangeAuthorDocumentTeamMemberDto },
	AppThunkAPIType
>(
	"document-workflow-definition/changeAuthorDocumentTeamMember",
	async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/team-members/change-author`,
				payload: dto,
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const removeDocumentTeamMemberFromWorkflow = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{ documentId: AppId; userName: string },
	AppThunkAPIType
>(
	"document-workflow-definition/removeDocumentTeamMemberFromWorkflow",
	async ({ documentId, userName }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/team-members/delete`,
				payload: {
					UserName: userName,
				}
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const addDocumentTeamMemberToStage = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{ documentId: AppId; stageId: AppId; userName: string },
	AppThunkAPIType
>(
	"document-workflow-definition/addDocumentTeamMemberToStage",
	async ({ documentId, stageId, userName }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/stages/${stageId}/team-members`,
				payload: {
					UserName: userName,
				},
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const removeDocumentTeamMemberFromStage = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{ documentId: AppId; stageId: AppId; userName: string },
	AppThunkAPIType
>(
	"document-workflow-definition/removeDocumentTeamMemberFromStage",
	async ({ documentId, stageId, userName }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/stages/${stageId}/team-members/delete`,
				payload: {
					UserName: userName,
				}
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const changeDocumentTeamMemberStages = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{
		documentId: AppId;
		documentTeamMemberId: AppId;
		dto: ChangeTeamMemberStagesDto;
	},
	AppThunkAPIType
>(
	"document-workflow-definition/changeDocumentTeamMemberStages",
	async (
		{ documentId, documentTeamMemberId, dto },
		{ dispatch, rejectWithValue }
	) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/team-members/${documentTeamMemberId}/stages`,
				payload: dto,
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const changeWorkflowActionDeadline = createAsyncThunk<
	DocumentWorkflowDefinitionDto,
	{ documentId: AppId; dto: ChangeWorkflowActionDeadlineDto },
	AppThunkAPIType
>(
	"document-workflow-definition/changeWorkflowActionDeadline",
	async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/change-deadline`,
				payload: {
					StageId: String(dto.stageId),
					Deadline: dto.deadline,
					ForAllMembers: dto.forAllMembers,
					DocumentTeamMemberId: dto.documentTeamMemberId
						? String(dto.documentTeamMemberId)
						: "",
				},
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return payload as DocumentWorkflowDefinitionDto;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);

export const sendWorkflowPendingActionReminder = createAsyncThunk<
	void,
	{ documentId: AppId; dto: SendWorkflowPendingActionReminderDto },
	AppThunkAPIType
>(
	"document-workflow-definition/sendWorkflowPendingActionReminder",
	async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(
			postWithAuth({
				url: `api/document-workflow-definition/${documentId}/send-reminder`,
				payload: {
					StageId: String(dto.stageId),
					ForAllMembers: dto.forAllMembers,
					DocumentTeamMemberId: dto.documentTeamMemberId
						? String(dto.documentTeamMemberId)
						: "",
				},
			})
		);

		const { payload } = result;

		if (postWithAuth.fulfilled.match(result)) {
			return;
		} else {
			return rejectWithValue(payload ?? { kind: "unknown" });
		}
	}
);
